import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";

const easeOutQuad = (t: number) => t * (2 - t);

interface ThriveScoreProps {
  startScore?: number;
  targetScore?: number;
  size?: number;
  className?: string;
  animate?: boolean;
  scoreTextSize?: string;
}

const ThriveScore: React.FC<ThriveScoreProps> = ({
  startScore = 0,
  targetScore,
  size = 250,
  className = "",
  animate = true,
  scoreTextSize = "text-6xl",
}) => {
  const [displayScore, setDisplayScore] = useState(startScore);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isFlashing, setIsFlashing] = useState(false);
  const [delayStep, setDelayStep] = useState(0.01);
  const flashDuration = 2;

  const animationRef = useRef<number | null>(null);
  const targetScoreRef = useRef(targetScore);
  const startScoreRef = useRef(startScore);
  const startTimeRef = useRef<number | null>(null);
  const flashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (targetScore !== undefined) {
      targetScoreRef.current = targetScore;
      startScoreRef.current = startScore;
      startTimeRef.current = null;

      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
      setIsFlashing(false);

      const step = (timestamp: number) => {
        if (!startTimeRef.current) {
          startTimeRef.current = timestamp;
          if (animate) { setIsAnimating(true); setDelayStep(0.04); }
        }
        const duration = 3000;
        const elapsed = timestamp - startTimeRef.current;
        const progress = Math.min(elapsed / duration, 1);
        const easedProgress = easeOutQuad(progress);
        const newScore = startScoreRef.current! + (targetScoreRef.current! - startScoreRef.current!) * easedProgress;
        setDisplayScore(newScore);

        if (progress < 1) {
          animationRef.current = requestAnimationFrame(step);
        } else {
          setDisplayScore(targetScoreRef.current!);
          setIsAnimating(false);
          setDelayStep(0.2);
          animationRef.current = null;
          setIsFlashing(true);
          flashTimeoutRef.current = setTimeout(() => setIsFlashing(false), flashDuration * 1000);
        }
      };

      if (animate) animationRef.current = requestAnimationFrame(step);
    } else {
      setDisplayScore(startScore);
      setIsAnimating(false);
      setDelayStep(0.2);
    }

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
    };
  }, [targetScore, animate, startScore]);

  const score = Math.round(targetScore !== undefined ? displayScore : startScore);
  const center = size / 2;
  const outerRadius = size / 2 - 4;
  const ringWidth = size * 0.08;
  const innerRadius = outerRadius - ringWidth - size * 0.04;
  
  // Orange shimmer arc (partial arc at top)
  const shimmerArcRadius = outerRadius + size * 0.04;

  return (
    <div
      className={`relative flex items-center justify-center ${className}`}
      style={{ width: size, height: size, overflow: 'visible' }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ overflow: 'visible' }}>
        {/* Orange shimmer glow arc - outer */}
        <circle
          cx={center}
          cy={center}
          r={shimmerArcRadius}
          fill="none"
          stroke="#F88250"
          strokeWidth={2}
          strokeDasharray={`${shimmerArcRadius * Math.PI * 0.6} ${shimmerArcRadius * Math.PI * 1.4}`}
          strokeDashoffset={shimmerArcRadius * Math.PI * 0.15}
          strokeLinecap="round"
          opacity={0.6}
          className={isAnimating ? "pulsating-active" : "pulsating-idle"}
          style={{ animationDelay: `${delayStep}s`, transformOrigin: "center" }}
        />

        {/* Blue ring outline */}
        <circle
          cx={center}
          cy={center}
          r={outerRadius}
          fill="none"
          stroke="hsl(221, 83%, 53%)"
          strokeWidth={ringWidth}
          className={isAnimating ? "pulsating-active" : "pulsating-idle"}
          style={{ animationDelay: `${2 * delayStep}s`, transformOrigin: "center" }}
        />

        {/* Orange accent arc on the ring */}
        <circle
          cx={center}
          cy={center}
          r={outerRadius}
          fill="none"
          stroke="#F88250"
          strokeWidth={ringWidth * 0.5}
          strokeDasharray={`${outerRadius * Math.PI * 0.35} ${outerRadius * Math.PI * 1.65}`}
          strokeDashoffset={outerRadius * Math.PI * -0.4}
          strokeLinecap="round"
          className={isAnimating ? "pulsating-active" : "pulsating-idle"}
          style={{ animationDelay: `${1.5 * delayStep}s`, transformOrigin: "center" }}
        />

        {/* Blue filled center */}
        <circle
          cx={center}
          cy={center}
          r={innerRadius}
          fill="hsl(221, 83%, 53%)"
          className={`surrounding-shadow ${isAnimating ? "pulsating-active" : "pulsating-idle"}`}
          style={{ animationDelay: `${0 * delayStep}s`, transformOrigin: "center" }}
        />
      </svg>

      {/* Score text */}
      <span
        className={`absolute font-extrabold text-white ${scoreTextSize}`}
        style={{ textShadow: "0 2px 8px rgba(0,0,0,0.2)" }}
      >
        {score}
      </span>

      {isFlashing && (
        <motion.div
          className="absolute rounded-full"
          style={{
            width: size,
            height: size,
            background: "radial-gradient(circle, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0) 70%)",
          }}
          initial={{ opacity: 1, scale: 1 }}
          animate={{ opacity: 0, scale: 1.3 }}
          transition={{ duration: flashDuration, ease: "easeOut" }}
        />
      )}
    </div>
  );
};

export { ThriveScore };
